import { getAutoTitle } from "@pages/Insights/ReportBuilder/ReportBuilderHelper/ReportBuilderHelper";
import { ReportFilterType } from "@pages/Insights/ReportBuilder/ReportFiltersListEditor/ReportFilterListEditor";
import { Utilities } from "../../../utils/utilities";
import { DataObjectHelper } from "../General/DataObjects/DataObjectHelper";
import { DataObjectHelpers } from "../General/DataObjects/DataObjectHelpers";
import { ReadAccess } from "../General/AccessData";
import { FirebaseData } from "../General/FirebaseData";
import { LocalData } from "../General/LocalData";
import { DataObjectField } from "../General/DataObjectField/DataObjectField";
import { FirebaseDataDateField } from "../General/DataObjectField/Generic/FirebaseDataDateField";
import { FirebaseDataNumberField } from "../General/DataObjectField/Generic/FirebaseDataNumberField";
import { ReportTypeType, ReportDisplayType, ReportGroupedByType, ChartOptions } from "@src/components/deprecated/Report/Report";

export class CustomReportData extends FirebaseData {
  readAccess?: ReadAccess = {};
  title?: string;
  dataTypeID?: string;
  private dataType?: DataObjectHelper<any>;
  reportType?: ReportTypeType;
  private display?: ReportDisplayType;
  private groupedBy?: ReportGroupedByType;
  private breakdownBy?: DataObjectField<any, any>;
  showChartLabels?: boolean;
  options: ChartOptions = {};
  filters?: ReportFilterType[] = [];

  setup(data: any): void {
    this.readAccess = Utilities.getReadAccess(data.readAccess);

    this.title = typeof data.title === "string" ? data.title : undefined;
    this.dataTypeID = typeof data.dataTypeID === "string" ? data.dataTypeID : undefined;
    this.reportType = typeof data.reportType === "string" ? data.reportType : undefined;

    this.display = undefined;
    this.groupedBy = undefined;
    this.breakdownBy = undefined;

    // Validating the new data structure for filters
    if (Array.isArray(data.filters) && data.filters.length > 0) {
      const filters: ReportFilterType[] = [];
      for (const filter of data.filters) {
        if (typeof filter.id === "string" && typeof filter.field === "string" && typeof filter.comparator === "string") {
          const value = ["rolling-back", "rolling-forward", "within-period"].includes(filter.comparator)
            ? filter.value
            : (this.getDataType()?.getField(filter.field)?.fullDecode(filter.value) ?? null);

          filters.push({
            id: filter.id,
            field: filter.field,
            comparator: filter.comparator,
            value: value,
          });
        }
      }
      this.filters = filters;
    } else if (typeof data.filters === "object") {
      if (
        typeof data.filters.dateRange === "object" &&
        typeof data.filters.dateRange.rollingBack === "number" &&
        typeof data.filters.dateProperty === "string"
      ) {
        const field = this.getDataType()?.getField(data.filters.dateProperty);
        if (field instanceof FirebaseDataDateField) {
          this.filters = [
            {
              id: "dateRange",
              field: data.filters.dateProperty,
              comparator: "rolling-back",
              value: data.filters.dateRange.rollingBack,
            },
          ];
        }
      }
    }

    this.options = {};
    if (typeof data.options === "object") {
      this.options.cumulative = typeof data.options.cumulative === "boolean" ? data.options.cumulative : undefined;
      this.options.comparedTo = typeof data.options.comparedTo === "string" ? data.options.comparedTo : undefined;
      this.options.displayTrend = typeof data.options.displayTrend === "string" ? data.options.displayTrend : undefined;
      this.options.sort = typeof data.options.sort === "string" ? data.options.sort : undefined;
      this.options.maxGroups = typeof data.options.maxGroups === "number" ? data.options.maxGroups : undefined;
      this.options.roundToDigits = typeof data.options.roundToDigits === "number" ? data.options.roundToDigits : undefined;
      this.options.hideOtherGroup = typeof data.options.hideOtherGroup === "boolean" ? data.options.hideOtherGroup : undefined;
    }

    this.showChartLabels = typeof data.showChartLabels === "boolean" ? data.showChartLabels : undefined;

    super.setup(data);
  }

  getDataType(): DataObjectHelper<any> | undefined {
    if (this.dataType) {
      return this.dataType;
    }
    if (this.dataTypeID) {
      if (this.dataTypeID.startsWith("collectionItems-")) {
        const collectionID = this.dataTypeID.replace("collectionItems-", "");
        this.dataType = DataObjectHelpers.getGlobalCollectionItemHelper(collectionID);
        return this.dataType;
      }
      this.dataType = DataObjectHelpers.getAllDataObjectHelpers().find((helper) => helper.id() === this.dataTypeID);
    }
    return this.dataType;
  }

  getTitle(): string {
    if (this.title) return this.title;
    if (!this.groupedBy) this.getGroupedBy();

    return getAutoTitle(this.getDataType() ?? DataObjectHelpers.empty, this.display, this.groupedBy);
  }

  getGroupedBy(): ReportGroupedByType | undefined {
    if (this.groupedBy) {
      return this.groupedBy;
    } else if (typeof this.rawData.groupedBy === "object" && this.rawData.groupedBy !== null && typeof this.rawData.groupedBy.field === "string") {
      const field = this.getDataType()
        ?.allFields()
        .find((field) => field.key === this.rawData.groupedBy.field);
      if (field) {
        if (field instanceof FirebaseDataDateField) {
          this.groupedBy = {
            field: field,
            frequency: typeof this.rawData.groupedBy.frequency === "string" ? this.rawData.groupedBy.frequency : undefined,
          };
        } else {
          this.groupedBy = { field: field };
        }
      }
    }
    return this.groupedBy;
  }

  getBreakdownBy(): DataObjectField<any, any> | undefined {
    if (this.breakdownBy) {
      return this.breakdownBy;
    } else if (typeof this.rawData.breakdownBy === "string") {
      this.breakdownBy = this.getDataType()
        ?.allFields()
        .find((field) => field.key === this.rawData.breakdownBy);
    }
    return this.breakdownBy;
  }

  getDisplay(): ReportDisplayType | undefined {
    if (this.display) {
      return this.display;
    } else if (typeof this.rawData.display === "object") {
      if (this.rawData.display.displayType === "countOfData") {
        this.display = "countOfData";
      } else if (this.rawData.display.displayType === "field") {
        const field = this.getDataType()
          ?.allFields()
          .find((field) => field.key === this.rawData.display.field);
        if (field instanceof FirebaseDataNumberField) {
          this.display = {
            field: field,
            calculation:
              typeof this.rawData.display.calculation === "string" && ["sum", "average"].includes(this.rawData.display.calculation)
                ? this.rawData.display.calculation
                : undefined,
          };
        }
      }
    }
    return this.display;
  }

  hasWriteAccess(): boolean {
    return this.createdBy?.user === LocalData.currentUser || Utilities.hasAccess(this.readAccess, undefined);
  }
}

export const convertGroupedBy = (groupedBy: ReportGroupedByType) => {
  return groupedBy.frequency ? { field: groupedBy.field?.key, frequency: groupedBy.frequency } : { field: groupedBy.field?.key };
};

type FirebaseDisplayData = {
  displayType: "countOfData" | "field";
  field?: string;
  calculation?: "sum" | "average" | null;
};

export const convertDisplay = (display: ReportDisplayType) => {
  let displayData: FirebaseDisplayData = { displayType: "countOfData" };

  if (display === "countOfData") {
    displayData = {
      displayType: "countOfData",
    };
  } else if (typeof display === "object" && display.field) {
    displayData = {
      displayType: "field",
      field: display.field.key,
      calculation: display.calculation ?? null,
    };
  }

  return displayData;
};

export const convertOptions = (options: ChartOptions) => {
  return {
    cumulative: options.cumulative ?? null,
    comparedTo: options.comparedTo ?? null,
    displayTrend: options.displayTrend ?? null,
    maxGroups: options.maxGroups ?? null,
    sort: options.sort ?? null,
    roundToDigits: options.roundToDigits ?? null,
    hideOtherGroup: options.hideOtherGroup ?? null,
  };
};

export const encodeFilters = (helper: DataObjectHelper<any>, filters: ReportFilterType[]) => {
  return filters.map((filter) => {
    return {
      id: filter.id,
      field: filter.field ?? "",
      comparator: filter.comparator ?? "",
      value: helper.getField(filter.field)?.fullEncode(filter.value) ?? null,
    };
  });
};

export const decodeFilters = (helper: DataObjectHelper<any>, filters: ReportFilterType[]): ReportFilterType[] => {
  return filters.map((filter) => {
    return {
      id: filter.id,
      field: filter.field,
      comparator: filter.comparator,
      value: helper.getField(filter.field)?.fullDecode(filter.value) ?? null,
    };
  });
};

